import { computed, ref, type Ref } from 'vue';
import { debounce } from 'lodash-es';
import { getCountryCodeByLanguage } from '@/helpers/language';

export interface ActivitiesLanguageItem {
  language_code: string;
  is_default?: boolean;
}

export interface MiscLanguageItem {
  code: string;
}

interface UseActivitiesLanguageParams<T extends ActivitiesLanguageItem> {
  actions: Ref<T[]>;
  index: Ref<number>;
  actionGroupIndex: Ref<number>;
  languagesFromStore: Ref<MiscLanguageItem[]>;
}

type DebouncedHandlers = Record<string, (value: unknown) => void>;

export const useActivitiesLanguage = <T extends ActivitiesLanguageItem>({
  actions,
  index,
  actionGroupIndex,
  languagesFromStore,
}: UseActivitiesLanguageParams<T>) => {
  const debouncedMessageHandlers = ref<DebouncedHandlers>({});

  const languages = computed(() => actions.value.map((action) => ({
    ...action,
    ...(languagesFromStore.value.find((language) => language.code === action.language_code) || {}),
    countryCode: getCountryCodeByLanguage(action.language_code)
  })));

  const selectedLanguages = computed(() => languages.value.map((language) => language.language_code));

  const defaultLanguageActionIndex = computed(() => actions.value.findIndex((action) => action.is_default));

  const defaultLanguage = computed<string>({
    get: () => {
      if (actionGroupIndex.value >= 0) {
        const defaultAction = actions.value.find((action) => action.is_default);
        if (defaultAction) {
          return defaultAction.language_code;
        }
      }

      return 'en';
    },
    set: (value) => {
      if (actionGroupIndex.value >= 0) {
        actions.value.forEach((action) => {
          action.is_default = false;
        });

        const defaultAction = actions.value.find((action) => action.language_code === value);
        if (defaultAction) {
          defaultAction.is_default = true;
        }
      }
    },
  });

  const handleSelectTab = (nextIndex: number) => {
    index.value = nextIndex;
  };

  const handleMakeDefault = (targetIndex: number) => {
    const target = languages.value[targetIndex];
    if (target?.language_code) {
      defaultLanguage.value = target.language_code;
    }
  };

  const getDebounceKey = (languageCode: string, field: string) => `${languageCode}_${field}`;

  const setActionsValue = (field: string, value: unknown) => {
    if (index.value === -1) {
      return;
    }

    const languageCode = actions.value[index.value]?.language_code;
    if (!languageCode) {
      return;
    }

    const key = getDebounceKey(languageCode, field);

    if (!debouncedMessageHandlers.value[key]) {
      debouncedMessageHandlers.value[key] = debounce((_value: unknown) => {
        const actionIndex = actions.value.findIndex((item) => item.language_code === languageCode);
        if (actionIndex !== -1 && actions.value[actionIndex]) {
          (actions.value[actionIndex] as Record<string, unknown>)[field] = _value;
        }
      }, 300);
    }

    debouncedMessageHandlers.value[key](value);
  };

  return {
    languages,
    selectedLanguages,
    defaultLanguageActionIndex,
    defaultLanguage,
    debouncedMessageHandlers,
    handleSelectTab,
    handleMakeDefault,
    getDebounceKey,
    setActionsValue,
  };
};
